import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Plus, Trash2, UserPlus } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Button, Card, Field } from "../components/ui";
import { CUSTOMER_ORIGINS } from "@yatta/domain";
import { useTeamData } from "../hooks/usePlatformData";
import { createCustomerCommand } from "../lib/customer-commands";
import { useOrganization } from "../auth/OrganizationProvider";

const dependentSchema = z.object({
  fullName: z.string().trim().min(3, "Informe o nome completo."),
  relationship: z.string().trim().min(2, "Informe o parentesco."),
});

const addressSchema = z
  .object({
    street: z.string().trim().optional(),
    number: z.string().trim().optional(),
    complement: z.string().trim().optional(),
    neighborhood: z.string().trim().optional(),
    city: z.string().trim().optional(),
    state: z.string().trim().optional(),
    postalCode: z.string().trim().optional(),
  })
  .refine(
    (address) => {
      const hasValue = Object.values(address).some(Boolean);
      return !hasValue || Boolean(address.street && address.city && address.state);
    },
    { message: "Informe rua, cidade e estado para salvar o endereço." },
  );

function isValidCpf(value: string) {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11 || /^([0-9])\1+$/.test(digits)) return false;
  const check = (length: number) => {
    let sum = 0;
    for (let index = 0; index < length; index += 1)
      sum += Number(digits[index]) * (length + 1 - index);
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };
  return check(9) === Number(digits[9]) && check(10) === Number(digits[10]);
}

const schema = z.object({
  holderName: z.string().trim().min(3, "Informe o nome completo."),
  cpf: z.string().trim().refine(isValidCpf, "Informe um CPF válido."),
  birthDate: z.string().optional(),
  phone: z.string().trim().min(10, "Informe um telefone válido."),
  email: z.union([z.literal(""), z.string().trim().email("Informe um e-mail válido.")]),
  sellerId: z.string().min(1, "Selecione o vendedor responsável."),
  customerOrigin: z.enum(CUSTOMER_ORIGINS, {
    message: "Escolha a origem do cliente.",
  }),
  address: addressSchema,
  dependents: z.array(dependentSchema),
});

type FormData = z.infer<typeof schema>;

export function CustomerCreatePage() {
  const navigate = useNavigate();
  const { activeOrganizationId } = useOrganization();
  const teamQuery = useTeamData();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      holderName: "",
      cpf: "",
      birthDate: "",
      phone: "",
      email: "",
      sellerId: "",
      customerOrigin: undefined,
      address: {
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        postalCode: "",
      },
      dependents: [],
    },
  });
  const dependents = useFieldArray({ control: form.control, name: "dependents" });

  const submit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      if (!activeOrganizationId) {
        throw new Error("Selecione uma organização ativa antes de cadastrar.");
      }
      const result = await createCustomerCommand({
        organizationId: activeOrganizationId,
        holder: {
          fullName: values.holderName,
          cpf: values.cpf.replace(/\D/g, ""),
          birthDate: values.birthDate,
          phone: values.phone,
          email: values.email,
        },
        dependents: values.dependents,
        sellerId: values.sellerId,
        customerOrigin: values.customerOrigin,
        address: Object.values(values.address).some(Boolean) ? values.address : undefined,
      });
      toast.success("Cliente cadastrado com sucesso.");
      navigate(`/clientes/${result.customerAccountId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível cadastrar o cliente.");
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="page-stack">
      <div className="page-back-link">
        <Link to="/clientes">
          <ArrowLeft size={16} /> Voltar para clientes
        </Link>
      </div>
      <Card
        title="Novo cliente"
        description="Cadastre uma família manualmente e atribua a carteira ao vendedor responsável."
        action={<UserPlus size={22} aria-hidden="true" />}
      >
        <form
          className="customer-create-form"
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <section className="form-section compact-form-section">
            <div className="section-heading">
              <div>
                <h2>Dados do titular</h2>
                <p className="muted">Esses dados identificam a família na base de clientes.</p>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Nome completo" error={form.formState.errors.holderName?.message}>
                <input {...form.register("holderName")} autoComplete="name" />
              </Field>
              <Field label="CPF" error={form.formState.errors.cpf?.message}>
                <input {...form.register("cpf")} inputMode="numeric" autoComplete="off" />
              </Field>
              <Field
                label="Data de nascimento (opcional)"
                error={form.formState.errors.birthDate?.message}
              >
                <input {...form.register("birthDate")} type="date" />
              </Field>
              <Field label="Telefone" error={form.formState.errors.phone?.message}>
                <input {...form.register("phone")} type="tel" autoComplete="tel" />
              </Field>
              <Field label="E-mail (opcional)" error={form.formState.errors.email?.message}>
                <input {...form.register("email")} type="email" autoComplete="email" />
              </Field>
              <Field label="Vendedor responsável" error={form.formState.errors.sellerId?.message}>
                <select {...form.register("sellerId")} disabled={teamQuery.isLoading}>
                  <option value="">Selecione um vendedor</option>
                  {(teamQuery.data ?? [])
                    .filter((member) => member.status === "ATIVO")
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} · {member.team}
                      </option>
                    ))}
                </select>
                {teamQuery.error && (
                  <span className="field-error">Não foi possível carregar a equipe.</span>
                )}
              </Field>
              <Field
                label="Origem do cliente"
                error={form.formState.errors.customerOrigin?.message}
              >
                <select {...form.register("customerOrigin")}>
                  <option value="">Selecione a origem</option>
                  <option value="PREEXISTING">Cliente preexistente</option>
                  <option value="NEW_ACQUISITION">Nova aquisição</option>
                </select>
              </Field>
            </div>
          </section>

          <section className="form-section compact-form-section">
            <div className="section-heading">
              <div>
                <h2>Endereço (opcional)</h2>
                <p className="muted">Você pode completar estes dados depois.</p>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Rua" error={form.formState.errors.address?.message}>
                <input {...form.register("address.street")} autoComplete="street-address" />
              </Field>
              <Field label="Número">
                <input {...form.register("address.number")} />
              </Field>
              <Field label="Complemento">
                <input {...form.register("address.complement")} />
              </Field>
              <Field label="Bairro">
                <input {...form.register("address.neighborhood")} />
              </Field>
              <Field label="Cidade">
                <input {...form.register("address.city")} autoComplete="address-level2" />
              </Field>
              <Field label="Estado">
                <input
                  {...form.register("address.state")}
                  maxLength={2}
                  autoComplete="address-level1"
                />
              </Field>
              <Field label="CEP">
                <input
                  {...form.register("address.postalCode")}
                  inputMode="numeric"
                  autoComplete="postal-code"
                />
              </Field>
            </div>
          </section>

          <section className="form-section compact-form-section">
            <div className="section-heading">
              <div>
                <h2>Dependentes</h2>
                <p className="muted">
                  Inclua os dependentes básicos agora; coberturas podem ser configuradas depois.
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => dependents.append({ fullName: "", relationship: "" })}
              >
                <Plus size={16} /> Adicionar dependente
              </Button>
            </div>
            {dependents.fields.length === 0 && (
              <p className="empty-inline">Nenhum dependente adicionado.</p>
            )}
            <div className="dependent-list">
              {dependents.fields.map((field, index) => (
                <div className="dependent-row" key={field.id}>
                  <Field
                    label={`Nome do dependente ${index + 1}`}
                    error={form.formState.errors.dependents?.[index]?.fullName?.message}
                  >
                    <input {...form.register(`dependents.${index}.fullName`)} />
                  </Field>
                  <Field
                    label="Parentesco"
                    error={form.formState.errors.dependents?.[index]?.relationship?.message}
                  >
                    <input
                      {...form.register(`dependents.${index}.relationship`)}
                      placeholder="Filho(a), cônjuge…"
                    />
                  </Field>
                  <button
                    type="button"
                    className="icon-button danger-icon"
                    aria-label={`Remover dependente ${index + 1}`}
                    onClick={() => dependents.remove(index)}
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <div className="card-actions">
            <Link className="button button-secondary" to="/clientes">
              Cancelar
            </Link>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Salvando…" : "Cadastrar cliente"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
